import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MovdMarketingHero from '../design-system/MovdMarketingHero.jsx';
import MovdInput from '../design-system/MovdInput.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const faqCategories = [
  {
    category: 'General Questions',
    questions: [
      { question: 'How does Movd work?', answer: 'Movd connects you with verified moving companies across Canada. Simply enter your moving details, receive instant quotes, compare options, and book your preferred mover - all through our secure platform.' },
      { question: 'Is Movd free to use?', answer: 'Yes! Movd is completely free for customers. You only pay the moving company directly for their services. There are no hidden fees or booking charges from Movd.' },
      { question: 'How are movers verified?', answer: 'All movers undergo thorough background checks including license verification, insurance confirmation, Better Business Bureau ratings, and customer review analysis before joining our platform.' }
    ]
  },
  {
    category: 'Booking & Pricing',
    questions: [
      { question: 'How quickly can I get quotes?', answer: "Most customers receive their first quote within 5 minutes. You'll typically have 3-5 competitive quotes within an hour of submitting your request." },
      { question: 'Are the quotes binding?', answer: 'We provide both binding and non-binding estimates depending on the mover. Binding estimates guarantee the final price, while non-binding estimates may change based on actual services required.' },
      { question: 'Can I modify or cancel my booking?', answer: 'Yes, you can modify or cancel your booking through your Movd dashboard. Cancellation policies vary by mover, but most offer free cancellation up to 48 hours before your move date.' }
    ]
  },
  {
    category: 'Insurance & Protection',
    questions: [
      { question: 'What insurance coverage is included?', answer: 'All Movd partner companies carry comprehensive liability and cargo insurance. Basic coverage is included, with options to purchase additional protection for high-value items.' },
      { question: 'What if something gets damaged?', answer: "In the rare event of damage, report it immediately to your mover and Movd support. We'll help facilitate the claims process and ensure you receive fair compensation according to the coverage selected." },
      { question: "Are there items movers won't transport?", answer: 'Hazardous materials, perishables, plants, and certain valuables may have restrictions. Your chosen mover will provide a comprehensive list of prohibited items during booking.' }
    ]
  },
  {
    category: 'Moving Day',
    questions: [
      { question: 'How should I prepare for moving day?', answer: "We'll send you a detailed preparation checklist after booking. Generally, ensure everything is packed (unless you've arranged packing services), be present for the pickup, and have important documents ready." },
      { question: 'Can I track my move in real-time?', answer: "Yes! Most of our partner movers provide real-time GPS tracking through the Movd app, so you can monitor your belongings' journey to your new home." },
      { question: 'What if the movers are late?', answer: 'While rare, delays can happen due to traffic or previous moves running long. Your mover will contact you with updates, and Movd support is available 24/7 to assist with any concerns.' }
    ]
  },
  {
    category: 'Support',
    questions: [
      { question: 'How can I contact customer support?', answer: 'Movd support is available 24/7 through live chat, phone at 1-800-MOVD-123, or email at support@movd.ca. We typically respond to emails within 2 hours.' },
      { question: "What if I'm not satisfied with my move?", answer: "We stand behind every move with our satisfaction guarantee. If you're not completely satisfied, contact us within 48 hours and we'll work to make it right, including potential refunds or re-moves." },
      { question: 'Do you service all of Canada?', answer: 'Yes! Movd operates in all provinces and territories across Canada, with the largest network of verified movers from Vancouver to Halifax and everywhere in between.' }
    ]
  }
];

export default function FAQPage() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return faqCategories;
    const q = search.toLowerCase();
    return faqCategories
      .map(cat => ({ ...cat, questions: cat.questions.filter(faq => faq.question.toLowerCase().includes(q) || faq.answer.toLowerCase().includes(q)) }))
      .filter(cat => cat.questions.length > 0);
  }, [search]);

  return (
    <div style={{ minHeight: '100vh' }}>
      <MovdMarketingHero eyebrow="Got questions" title="Frequently asked questions" subtitle="Find answers to common questions about moving with Movd." />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: 'clamp(24px, 5vw, 48px) 20px' }}>
        <MovdInput label="Search" placeholder="Search questions..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 32 }} />

        {filteredCategories.length === 0 && <p style={{ textAlign: 'center', color: movdTokens.color.content.secondary, padding: '32px 0' }}>No results for &quot;{search}&quot; — try different keywords.</p>}

        {filteredCategories.map((category, categoryIndex) => (
          <div key={category.category} style={{ marginBottom: 32 }}>
            <h2 style={{ margin: '0 0 16px', fontWeight: 800, fontSize: 20, paddingLeft: 12, borderLeft: `4px solid ${movdTokens.color.state.warning}` }}>{category.category}</h2>
            {category.questions.map((faq, faqIndex) => {
              const panelId = `${categoryIndex}-${faqIndex}`;
              const isOpen = expanded === panelId;
              return (
                <div key={panelId} style={{ marginBottom: 8, borderRadius: 10, overflow: 'hidden', background: movdTokens.color.action.primary, color: '#fff' }}>
                  <button type="button" onClick={() => setExpanded(isOpen ? null : panelId)} aria-expanded={isOpen} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontWeight: 700, fontSize: 17, font: 'inherit' }}>
                    {faq.question}
                    <span aria-hidden="true" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>▾</span>
                  </button>
                  {isOpen && <div style={{ padding: '0 20px 20px', lineHeight: 1.6 }}>{faq.answer}</div>}
                </div>
              );
            })}
          </div>
        ))}

        <div style={{ marginTop: 32, padding: 'clamp(24px, 5vw, 40px)', textAlign: 'center', borderRadius: 12, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', border: '1px solid #e2e8f0' }}>
          <h2 style={{ margin: '0 0 8px', fontWeight: 800, fontSize: 22 }}>Still have questions?</h2>
          <p style={{ margin: '0 0 20px', color: movdTokens.color.content.secondary, maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }}>Our customer support team is available 24/7 to help with any questions about your move.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <MovdButton variant="secondary" onClick={() => navigate('/contact')}>Contact support</MovdButton>
            <MovdButton onClick={() => navigate('/')}>Get your quote</MovdButton>
          </div>
        </div>
      </div>
    </div>
  );
}
